import { getRequest, postRequest, deleteRequest } from './http.service';
import {
  URL_ONEDRIVE_BROWSE,
  URL_ONEDRIVE_LINKS,
  URL_ONEDRIVE_LINKS_FOR,
  URL_ONEDRIVE_LINK_BY_ID,
  URL_ONEDRIVE_LINK_SYNC,
  URL_ONEDRIVE_RESOLVE_SHARE,
} from './url.service';

/** One entry from the advisor's OneDrive, as returned by the browse endpoint. */
export interface OneDriveBrowseItem {
  itemId: string;
  name: string;
  isFolder: boolean;
  childCount?: number;
  mimeType?: string;
  size?: number;
  webUrl?: string;
  /**
   * The drive this item actually lives in. Equal to the caller's own drive for
   * their own files, and the sharer's drive for a shared folder — which is why
   * it must be carried back to the server rather than assumed.
   */
  driveId?: string;
  /** Shared with the advisor rather than owned by them. */
  isShared?: boolean;
}

export interface OneDriveBrowseResult {
  success: boolean;
  driveId: string;
  driveName: string;
  items: OneDriveBrowseItem[];
}

export interface OneDriveLinkSummary {
  driveItemId: string;
  name: string;
  path: string;
  webUrl?: string;
  ownerUserId: string;
  lastSyncedAt?: string;
}

/**
 * These endpoints return a bare `{ success, ... }` shape rather than the
 * `{ hasError }` envelope the /drive endpoints use, so callers must not copy
 * the `response.hasError === false` check from useFileExplorerData.
 */

/**
 * List the caller's OneDrive, including folders shared with them. Omit itemId
 * for the drive root; pass driveId alongside it when opening a shared folder.
 */
export const browseOneDrive = (itemId?: string, driveId?: string): Promise<OneDriveBrowseResult> =>
  getRequest(URL_ONEDRIVE_BROWSE(itemId, driveId));

/**
 * Link a OneDrive folder or file into Prospo.
 *
 * Destination: `clientId` puts it at that client's Files root; `parentId` puts
 * it in a specific folder, for the levels of Shared Files that sit above any
 * client. Pass one of the two.
 *
 * driveId defaults server-side to the caller's own drive and is required for
 * anything shared. Either way the server reads it with the caller's own token,
 * so this cannot reach a drive they couldn't already open in OneDrive.
 */
export const linkOneDriveFolder = (
  itemId: string,
  clientId: string | undefined,
  driveId?: string,
  parentId?: string,
) => postRequest(URL_ONEDRIVE_LINKS, { itemId, clientId, driveId, parentId });

/** What a sharing link turned out to point at. */
export interface ResolvedShare {
  success: boolean;
  itemId: string;
  driveId: string;
  name: string;
  /** Both are linkable — a colleague shares one document far more often than the folder around it. */
  isFolder: boolean;
  childCount?: number;
  mimeType?: string;
  size?: number;
  webUrl?: string;
}

/**
 * Resolve a OneDrive/SharePoint sharing link to a folder that can be linked.
 *
 * Needed because a folder shared from a SharePoint site is in nobody's
 * OneDrive: browsing cannot find it, and Microsoft does not report it among
 * "shared with me" either, even though the OneDrive website lists it. Pasting
 * the link is the only way to name it.
 */
export const resolveOneDriveShare = (url: string): Promise<ResolvedShare> =>
  postRequest(URL_ONEDRIVE_RESOLVE_SHARE, { url });

export const fetchOneDriveLinks = (clientId: string): Promise<{ success: boolean; links: OneDriveLinkSummary[] }> =>
  getRequest(URL_ONEDRIVE_LINKS_FOR(clientId));

/** Force a refresh, bypassing the server's one-minute read throttle. */
export const syncOneDriveLink = (driveItemId: string) =>
  postRequest(URL_ONEDRIVE_LINK_SYNC(driveItemId), {});

/** Removes Prospo's reference only — the advisor's real OneDrive is untouched. */
export const unlinkOneDriveFolder = (driveItemId: string) =>
  deleteRequest(URL_ONEDRIVE_LINK_BY_ID(driveItemId));
